import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { V4FormRow, V4Group, V4Row } from '../ux-v4.js';
import { addSuppression, listSuppression, removeSuppression, type SuppressionDto } from '../../lib/acquisition-radar-api.js';
import { formatDate } from './labels.js';

/**
 * Krok AKV1 — Vylouceni: adresy a lide, kteri nechteji byt oslovovani.
 * Radar je do tiskove sady nikdy nepusti (per kancelar). Zaznamy vznikaji
 * i namitkou adresata z dopisu — ty maji jen normalizovanou adresu.
 */

const tr = (key: MessageKey, values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

function errorText(error: unknown): string {
  return error instanceof Error && error.message !== '' ? error.message : tr('webAdmin.acquisitionRadar.saveError');
}

export function SuppressionTab(): React.JSX.Element {
  const [items, setItems] = React.useState<readonly SuppressionDto[] | null>(null);
  const [address, setAddress] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    listSuppression()
      .then(setItems)
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  const add = (): void => {
    setError(null);
    addSuppression(address.trim(), note.trim() === '' ? null : note.trim())
      .then((created) => {
        setItems((prev) => [...(prev ?? []).filter((x) => x.id !== created.id), created]);
        setAddress('');
        setNote('');
      })
      .catch((e: unknown) => setError(errorText(e)));
  };

  const remove = (item: SuppressionDto): void => {
    if (!window.confirm(tr('webAdmin.acquisitionRadar.suppression.confirmRemove', { address: item.address }))) return;
    removeSuppression(item.id)
      .then(() => setItems((prev) => prev?.filter((x) => x.id !== item.id) ?? null))
      .catch((e: unknown) => setError(errorText(e)));
  };

  return (
    <div className="rh-v4-groups" data-testid="akv-suppression">
      <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.suppression.perex')}</p>
      {error !== null ? (
        <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-suppression-error">
          {error}
        </p>
      ) : null}
      {items === null && error === null ? <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p> : null}
      {items !== null && items.length === 0 ? (
        <p className="rh-akv-hint" data-testid="akv-suppression-empty">
          {tr('webAdmin.acquisitionRadar.suppression.empty')}
        </p>
      ) : null}
      {items !== null && items.length > 0 ? (
        <V4Group label={tr('webAdmin.acquisitionRadar.suppression.listLabel')} testId="akv-suppression-list">
          {items.map((item) => (
            <V4Row
              key={item.id}
              testId={`akv-suppression-${String(item.id)}`}
              title={item.address}
              sub={[item.note, formatDate(item.createdAt)].filter((p): p is string => p !== null && p !== '').join(' · ')}
              right={
                <button
                  type="button"
                  className="rh-v4-btn rh-v4-btn--chip"
                  onClick={() => remove(item)}
                  data-testid={`akv-suppression-remove-${String(item.id)}`}
                >
                  {tr('webAdmin.acquisitionRadar.suppression.remove')}
                </button>
              }
            />
          ))}
        </V4Group>
      ) : null}
      <V4Group label={tr('webAdmin.acquisitionRadar.suppression.addTitle')} testId="akv-suppression-form">
        <V4FormRow label={tr('webAdmin.acquisitionRadar.suppression.address')}>
          <input
            className="rh-input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            aria-label={tr('webAdmin.acquisitionRadar.suppression.address')}
            data-testid="akv-suppression-address"
          />
        </V4FormRow>
        <V4FormRow label={tr('webAdmin.acquisitionRadar.suppression.note')}>
          <input
            className="rh-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={tr('webAdmin.acquisitionRadar.suppression.note')}
            data-testid="akv-suppression-note"
          />
        </V4FormRow>
        <div className="rh-akv-detail">
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled"
            disabled={address.trim().length < 3}
            onClick={add}
            data-testid="akv-suppression-add"
          >
            {tr('webAdmin.acquisitionRadar.suppression.add')}
          </button>
        </div>
      </V4Group>
    </div>
  );
}
